import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CreditCard, ImagePlus, Loader2, Plus, Sparkles } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useBusiness, usePermission } from '@/features/auth/auth.store';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useBusinessInfo } from './business-info.api';
import {
  CHARGE_TYPES,
  DEFAULT_GROUPS,
  PAYMENT_TYPES,
  PROVIDERS,
  SELECTORS,
  blankPayment,
  copyOfPayment,
  toPaymentInput,
  useCreateDefaultPayments,
  useCreatePaymentOption,
  useDeletePaymentOption,
  usePaymentOptions,
  useUpdatePaymentOption,
  useUploadPaymentImage,
  type DefaultPaymentsInput,
  type PaymentOption,
  type PaymentOptionInput,
} from './payment-config.api';

/**
 * The ways this store can be paid.
 *
 * The list answers the question that matters at a glance — which tenders are
 * live, and where each one is accepted — because a payment option that exists
 * but is switched off everywhere looks identical to one that works until a
 * customer tries it.
 */

/**
 * The enum labels.
 *
 * Providers and selectors are brand and card-network names that read the same
 * either way, so they stay literal. Charge types and payment types are phrases
 * — "up front", "cash", "not selected" — so they are keyed by index and
 * translated.
 */
function useEnumLabels() {
  const { t } = useTranslation();
  return {
    providers: PROVIDERS,
    selectors: SELECTORS,
    chargeTypes: CHARGE_TYPES.map((_, index) =>
      t(`paymentConfig.charge_${index}`)
    ),
    paymentTypes: PAYMENT_TYPES.map((_, index) =>
      t(`paymentConfig.pt_${index}`)
    ),
  };
}

/** Where an option may be used. The five the API tracks. */
const SERVICES = [
  'allowedInRegularService',
  'allowedInDeliveryService',
  'allowedInDeliService',
  'allowedInPosService',
  'allowedInSelfCheckoutService',
] as const;

export function PaymentConfigPage() {
  const { t } = useTranslation();
  const business = useBusiness();
  const isAdmin = usePermission('isAdmin');

  const labels = useEnumLabels();

  const info = useBusinessInfo(business?.id ?? null);
  const locationId = info.data?.locations?.[0]?.id ?? null;
  const options = usePaymentOptions(locationId);

  const [editing, setEditing] = useState<PaymentOption | 'new' | null>(null);
  const [defaults, setDefaults] = useState(false);

  const rows = options.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('paymentConfig.title')}
        description={business?.name ?? t('paymentConfig.subtitle')}
        actions={
          isAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={!business}
                onClick={() => setDefaults(true)}
              >
                <Sparkles />
                <span className="hidden sm:inline">
                  {t('paymentConfig.createDefaults')}
                </span>
              </Button>
              <Button
                size="sm"
                disabled={!locationId}
                onClick={() => setEditing('new')}
              >
                <Plus />
                <span className="hidden sm:inline">
                  {t('paymentConfig.newOption')}
                </span>
              </Button>
            </>
          )
        }
      />

      {/* `isLoading` on both: the options wait on the store's location, and
          a store without one would otherwise hold the skeleton forever. */}
      {(info.isLoading || options.isLoading) && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-xl" />
          ))}
        </div>
      )}

      {(info.isError || options.isError) && (
        <Empty>{t('paymentConfig.loadFailed')}</Empty>
      )}

      {options.isSuccess && rows.length === 0 && (
        <Empty busy={options.isFetching}>
          {t('paymentConfig.noOptions')}
        </Empty>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={options.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              options.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setEditing(option)}
                className="hover:bg-accent/60 flex w-full items-start gap-3 p-3 text-left transition-colors"
              >
                {option.imageUrl ? (
                  <img
                    src={option.imageUrl}
                    alt=""
                    className="size-10 shrink-0 rounded-lg border object-contain p-1"
                  />
                ) : (
                  <span className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-lg border">
                    <CreditCard className="size-4" />
                  </span>
                )}

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate text-sm font-medium">
                      {option.name || t('paymentConfig.unnamed')}
                    </span>
                    <Badge variant={option.isActive ? 'success' : 'secondary'}>
                      {option.isActive
                        ? t('paymentConfig.active')
                        : t('paymentConfig.inactive')}
                    </Badge>
                    {option.offlineOnly && (
                      <Badge variant="secondary">
                        {t('paymentConfig.offlineOnly')}
                      </Badge>
                    )}
                  </div>

                  <p className="text-muted-foreground truncate text-xs">
                    {labels.paymentTypes[option.paymentType]
                      ?? option.paymentType}{' '}
                    · {labels.providers[option.provider] ?? option.provider}
                    {option.minCharge > 0
                      && ` · ${t('paymentConfig.from')} ${formatCurrency(option.minCharge)}`}
                  </p>

                  {/* Where it is accepted, or a warning that it is nowhere. */}
                  <div className="flex flex-wrap gap-1">
                    {SERVICES.filter((service) => option[service]).map(
                      (service) => (
                        <Badge key={service} variant="outline">
                          {t(`paymentConfig.${service}`)}
                        </Badge>
                      )
                    )}
                    {SERVICES.every((service) => !option[service]) && (
                      <span className="text-muted-foreground text-xs">
                        {t('paymentConfig.nowhere')}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <PaymentEditorSheet
        option={editing}
        sources={rows}
        locationId={locationId}
        canEdit={isAdmin}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />

      <DefaultsSheet
        businessId={business?.id ?? null}
        open={defaults}
        onOpenChange={setDefaults}
      />
    </div>
  );
}

function Empty({
  children,
  busy,
}: {
  children: React.ReactNode;
  /** A refetch in flight: there are no rows to dim, so the note gets it. */
  busy?: boolean;
}) {
  return (
    <div className="relative rounded-xl">
      <BusyOverlay show={busy} />
      <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
        {children}
      </p>
    </div>
  );
}

function PaymentEditorSheet({
  option,
  sources,
  locationId,
  canEdit,
  open,
  onOpenChange,
}: {
  option: PaymentOption | 'new' | null;
  /** Existing options a new one can be started from. */
  sources: PaymentOption[];
  locationId: string | null;
  canEdit: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const labels = useEnumLabels();
  const create = useCreatePaymentOption();
  const update = useUpdatePaymentOption();
  const remove = useDeletePaymentOption();
  const upload = useUploadPaymentImage();

  const existing = option !== 'new' ? option : null;
  const [draft, setDraft] = useState<PaymentOptionInput | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);
  const [copiedFrom, setCopiedFrom] = useState('');

  const key = existing?.id ?? (option === 'new' ? 'new' : null);
  if (option && hydrated !== key) {
    setHydrated(key);
    setCopiedFrom('');
    setDraft(
      existing
        ? toPaymentInput(existing)
        : blankPayment(locationId ?? '')
    );
  }

  if (!draft || !option) return null;

  const set = (patch: Partial<PaymentOptionInput>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const busy = create.isPending || update.isPending || remove.isPending;
  const failed = () => toast.error(t('paymentConfig.actionFailed'));

  function save() {
    const done = {
      onSuccess: () => {
        toast.success(t('paymentConfig.saved'));
        onOpenChange(false);
      },
      onError: failed,
    };

    if (existing) update.mutate({ id: existing.id, input: draft! }, done);
    else create.mutate(draft!, done);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {existing
              ? t('paymentConfig.editOption')
              : t('paymentConfig.newOption')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          {/* Most options are near-copies of one already set up — the same
              processor and tokens, differing in where they are accepted. */}
          {!existing && sources.length > 0 && (
            <div className="bg-muted/40 space-y-1.5 rounded-xl border p-3">
              <Label>{t('paymentConfig.createFrom')}</Label>
              <Select
                value={copiedFrom}
                disabled={!canEdit}
                onValueChange={(id) => {
                  const source = sources.find((row) => row.id === id);
                  if (!source) return;
                  setCopiedFrom(id);
                  setDraft(
                    copyOfPayment(
                      source,
                      locationId ?? '',
                      t('paymentConfig.copyName', {
                        name: source.name || t('paymentConfig.unnamed'),
                      })
                    )
                  );
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={t('paymentConfig.createFromPlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name || t('paymentConfig.unnamed')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                {t('paymentConfig.createFromHint')}
              </p>
            </div>
          )}

          <Field label={t('paymentConfig.name')}>
            <Input
              value={draft.name ?? ''}
              disabled={!canEdit}
              onChange={(event) => set({ name: event.target.value })}
            />
          </Field>

          <div className="space-y-1.5">
            <Label>{t('paymentConfig.image')}</Label>
            <div className="flex items-center gap-3">
              {draft.imageUrl && (
                <img
                  src={draft.imageUrl}
                  alt=""
                  className="size-12 shrink-0 rounded-lg border object-contain p-1"
                />
              )}
              <Input
                value={draft.imageUrl ?? ''}
                disabled={!canEdit}
                placeholder={t('paymentConfig.imageUrl')}
                onChange={(event) => set({ imageUrl: event.target.value })}
              />
              <Button
                variant="outline"
                disabled={!canEdit || upload.isPending}
                onClick={() =>
                  document.getElementById('payment-image')?.click()
                }
              >
                {upload.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <ImagePlus className="size-4" />
                )}
              </Button>
              <input
                id="payment-image"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  upload.mutate(file, {
                    onSuccess: (url) => set({ imageUrl: url }),
                    onError: () =>
                      toast.error(t('paymentConfig.uploadFailed')),
                  });
                }}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('paymentConfig.paymentType')}>
              <Enum
                labels={labels.paymentTypes}
                value={draft.paymentType}
                disabled={!canEdit}
                onChange={(paymentType) => set({ paymentType })}
              />
            </Field>
            <Field label={t('paymentConfig.provider')}>
              <Enum
                labels={labels.providers}
                value={draft.provider}
                disabled={!canEdit}
                onChange={(provider) => set({ provider })}
              />
            </Field>
            <Field label={t('paymentConfig.selector')}>
              <Enum
                labels={labels.selectors}
                value={draft.selector}
                disabled={!canEdit}
                onChange={(selector) => set({ selector })}
              />
            </Field>
            <Field label={t('paymentConfig.chargeType')}>
              <Enum
                labels={labels.chargeTypes}
                value={draft.chargeType}
                disabled={!canEdit}
                onChange={(chargeType) => set({ chargeType })}
              />
            </Field>
            <Field
              label={t('paymentConfig.minCharge')}
              hint={t('paymentConfig.minChargeHint')}
            >
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={draft.minCharge}
                disabled={!canEdit}
                onChange={(event) =>
                  set({ minCharge: Number(event.target.value) })
                }
              />
            </Field>
          </div>

          <Group title={t('paymentConfig.whereAccepted')}>
            {SERVICES.map((service) => (
              <Toggle
                key={service}
                label={t(`paymentConfig.${service}`)}
                checked={draft[service]}
                disabled={!canEdit}
                onChange={(value) => set({ [service]: value })}
              />
            ))}
          </Group>

          <Group title={t('paymentConfig.rules')}>
            <Toggle
              label={t('paymentConfig.isActive')}
              hint={t('paymentConfig.isActiveHint')}
              checked={draft.isActive}
              disabled={!canEdit}
              onChange={(isActive) => set({ isActive })}
            />
            <Toggle
              label={t('paymentConfig.offlineOnly')}
              checked={draft.offlineOnly}
              disabled={!canEdit}
              onChange={(offlineOnly) => set({ offlineOnly })}
            />
            <Toggle
              label={t('paymentConfig.taxExcluded')}
              checked={draft.taxExcluded}
              disabled={!canEdit}
              onChange={(taxExcluded) => set({ taxExcluded })}
            />
            <Toggle
              label={t('paymentConfig.allowNonFood')}
              checked={draft.allowNonFoodProducts}
              disabled={!canEdit}
              onChange={(allowNonFoodProducts) =>
                set({ allowNonFoodProducts })
              }
            />
            <Toggle
              label={t('paymentConfig.allowAlcohol')}
              checked={draft.allowAlcoholicItems}
              disabled={!canEdit}
              onChange={(allowAlcoholicItems) => set({ allowAlcoholicItems })}
            />
            <Toggle
              label={t('paymentConfig.allowTransfers')}
              checked={draft.allowTransfers}
              disabled={!canEdit}
              onChange={(allowTransfers) => set({ allowTransfers })}
            />
            <Toggle
              label={t('paymentConfig.allowServiceFee')}
              checked={draft.allowsServiceFeePayment}
              disabled={!canEdit}
              onChange={(allowsServiceFeePayment) =>
                set({ allowsServiceFeePayment })
              }
            />
            <Toggle
              label={t('paymentConfig.ignoreOrderCap')}
              checked={draft.ignoreOrderCap}
              disabled={!canEdit}
              onChange={(ignoreOrderCap) => set({ ignoreOrderCap })}
            />
            <Toggle
              label={t('paymentConfig.freeDelivery')}
              checked={draft.hasFreeDelivery}
              disabled={!canEdit}
              onChange={(hasFreeDelivery) => set({ hasFreeDelivery })}
            />
            <Toggle
              label={t('paymentConfig.needsBillingAgreement')}
              checked={draft.needsBillingAgreement}
              disabled={!canEdit}
              onChange={(needsBillingAgreement) =>
                set({ needsBillingAgreement })
              }
            />
            <Toggle
              label={t('paymentConfig.openCashDrawer')}
              hint={t('paymentConfig.openCashDrawerHint')}
              checked={draft.shouldOpenCashDrawer}
              disabled={!canEdit}
              onChange={(shouldOpenCashDrawer) =>
                set({ shouldOpenCashDrawer })
              }
            />
          </Group>

          <Group title={t('paymentConfig.credentials')}>
            <Field label={t('paymentConfig.publicToken')}>
              <Input
                value={draft.publicToken ?? ''}
                disabled={!canEdit}
                onChange={(event) => set({ publicToken: event.target.value })}
              />
            </Field>
            <Field label={t('paymentConfig.privateToken')}>
              <Input
                type="password"
                autoComplete="off"
                value={draft.privateToken ?? ''}
                disabled={!canEdit}
                onChange={(event) => set({ privateToken: event.target.value })}
              />
            </Field>
            <Field label={t('paymentConfig.transferToken')}>
              <Input
                type="password"
                autoComplete="off"
                value={draft.transferToken ?? ''}
                disabled={!canEdit}
                onChange={(event) => set({ transferToken: event.target.value })}
              />
            </Field>
          </Group>

          <Group title={t('paymentConfig.instructions')}>
            <Field label={t('paymentConfig.instructionsEn')}>
              <Input
                value={draft.specialInstrctionsEng ?? ''}
                disabled={!canEdit}
                onChange={(event) =>
                  set({ specialInstrctionsEng: event.target.value })
                }
              />
            </Field>
            <Field label={t('paymentConfig.instructionsEs')}>
              <Input
                value={draft.specialInstrctionsSpa ?? ''}
                disabled={!canEdit}
                onChange={(event) =>
                  set({ specialInstrctionsSpa: event.target.value })
                }
              />
            </Field>
          </Group>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            {existing && canEdit ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={busy}>
                    {remove.isPending && <Loader2 className="animate-spin" />}
                    {t('paymentConfig.delete')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('paymentConfig.delete')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('paymentConfig.deletePrompt', {
                        name: existing.name || t('paymentConfig.unnamed'),
                      })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() =>
                        remove.mutate(existing.id, {
                          onSuccess: () => {
                            toast.success(t('paymentConfig.deleted'));
                            onOpenChange(false);
                          },
                          onError: failed,
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <span />
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                disabled={!canEdit || busy || !draft.name?.trim()}
                onClick={save}
              >
                {(create.isPending || update.isPending) && (
                  <Loader2 className="animate-spin" />
                )}
                {existing ? t('common.save') : t('paymentConfig.create')}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Lay down a starting set of payment options.
 *
 * Additive: it creates what is ticked and leaves anything already configured
 * alone, so it is the fastest way to open a new store and a safe thing to run
 * twice.
 */
function DefaultsSheet({
  businessId,
  open,
  onOpenChange,
}: {
  businessId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateDefaultPayments();

  const [picked, setPicked] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  function submit() {
    const body = { businessId: businessId! } as DefaultPaymentsInput;
    for (const group of Object.values(DEFAULT_GROUPS)) {
      for (const key of group) {
        body[key] = picked.has(key) as never;
      }
    }

    create.mutate(body, {
      onSuccess: () => {
        toast.success(t('paymentConfig.defaultsCreated'));
        setPicked(new Set());
        onOpenChange(false);
      },
      onError: () => toast.error(t('paymentConfig.actionFailed')),
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('paymentConfig.createDefaults')}</SheetTitle>
          <p className="text-muted-foreground text-sm">
            {t('paymentConfig.createDefaultsHint')}
          </p>
        </SheetHeader>

        <div className="space-y-3 p-4">
          {(
            Object.entries(DEFAULT_GROUPS) as [
              keyof typeof DEFAULT_GROUPS,
              readonly string[],
            ][]
          ).map(([group, keys]) => (
            <Group key={group} title={t(`paymentConfig.group_${group}`)}>
              {keys.map((key) => (
                <Toggle
                  key={key}
                  label={t(`paymentConfig.${key}`)}
                  checked={picked.has(key)}
                  onChange={() => toggle(key)}
                />
              ))}
            </Group>
          ))}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={create.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              disabled={create.isPending || picked.size === 0}
              onClick={submit}
            >
              {create.isPending && <Loader2 className="animate-spin" />}
              {t('paymentConfig.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Group({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2 rounded-xl border p-3">
      <h2 className="text-sm font-medium">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border px-3 py-2',
        disabled && 'opacity-60'
      )}
    >
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        {hint && (
          <span className="text-muted-foreground block text-xs">{hint}</span>
        )}
      </span>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </label>
  );
}

/** An enum whose values are its indices. */
function Enum({
  labels,
  value,
  onChange,
  disabled,
}: {
  labels: readonly string[];
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  return (
    <Select
      value={String(value)}
      disabled={disabled}
      onValueChange={(next) => onChange(Number(next))}
    >
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {labels.map((label, index) => (
          <SelectItem key={label} value={String(index)}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
